'use client'

import { useCallback } from 'react'

import { usePathname, useSearchParams } from 'next/navigation'
import { styled } from 'styled-components'

import { useFlag } from '@b-stock/fe-core/bstock-next/helpers'
import {
  GlobalHeader,
  type GlobalHeaderAnalyticsEvent,
  type GlobalHeaderNavData,
} from '@b-stock/fe-core/bstock-react'
import { Breakpoints } from '@b-stock/fe-core/design-system'

import type { MarketplaceAuctionNav } from '@/helpers/cms/marketplaceNav'
import { FeatureFlags } from '@/helpers/featureFlags'
import { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'
import { useLogoClickHandler } from '@/helpers/useLogoClickHandler'
import { useMenuItemAnalytics } from '@/helpers/useMenuItemAnalytics'

import GlobalHeaderAuctionNavigation from './GlobalHeaderAuctionNavigation'
import { useAuthProps } from './useAuthProps'
import { useNotifications } from './useNotifications'
import { useOpenDropdownState } from './useOpenDropdownState'
import { usePortalName } from './usePortalName'
import { useSavedSearch } from './useSavedSearch'
import { useUniversalSearch } from './useUniversalSearch'
import { useWatchlist } from './useWatchlist'

// Override fe-core's GlobalHeader border-bottom and layout vars for home-portal,
// matching the existing PrimaryHeader override. fe-core's HeaderContainer hard-
// sets --maxPageWidth:75rem and (at min.large) --headerPadding:3rem on itself;
// we widen the page and pin a direct max-width on the inner HeaderDiv so the
// override holds even if SSR style-injection ordering beats the variable path.
const GlobalHeaderStyleOverride = styled.div`
  & > header {
    border-bottom: none;
    --maxPageWidth: 90rem !important;
  }

  @media ${Breakpoints.min.large} {
    & > header {
      --headerPadding: 1.5rem !important;
    }

    & > header > div {
      max-width: calc(90rem + 1.5rem * 2);
    }
  }
`

type GlobalHeaderWrapperProps = {
  /** Variant determines which exclusion/portal logic to use */
  variant: 'default' | 'storefront'

  /** Show the auction navigation second row. Independent of `variant`. */
  showAuctionNav?: boolean

  /**
   * Nav content from the CMS. Fetched in the root layout, which also evaluates the flag gating it,
   * so `undefined` here means the flag is off or the CMS had nothing — fe-core falls back to its
   * bundled `navData.json` per field either way.
   */
  navData?: GlobalHeaderNavData

  /** Second-row nav content from the CMS. Same fetch and flag as `navData`. */
  auctionNav?: MarketplaceAuctionNav
}

export function GlobalHeaderWrapper({
  variant,
  showAuctionNav = false,
  navData,
  auctionNav,
}: GlobalHeaderWrapperProps) {
  const searchProps = useUniversalSearch(variant)
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const { trackButtonClicked } = useAnalytics()

  const authProps = useAuthProps()
  const { accountId, personId, isAuthenticated } = authProps.user

  const portalName = usePortalName(variant)

  const isAlpAsFrontpage = useFlag(FeatureFlags.ALP_AS_FRONTPAGE)
  const showOffersLink = useFlag(FeatureFlags.ENABLE_OFFERS_PAGE_FOR_BUYERS)
  const showInventoryLink = useFlag(FeatureFlags.ENABLE_CROSSLISTER)

  const handleLogoClick = useLogoClickHandler(isAlpAsFrontpage)

  const handleMenuItemClick = useMenuItemAnalytics()

  const handleAnalyticsEvent = useCallback(
    (event: GlobalHeaderAnalyticsEvent) => {
      if (event.eventName === 'button_click') {
        const { button_name: buttonName } = event.data
        if (typeof buttonName !== 'string') return
        if (buttonName === 'global_header_watchlist') {
          trackButtonClicked({
            buttonName: 'global_header_watchlist',
          })
        } else if (buttonName === 'global_header_saved_search') {
          trackButtonClicked({
            buttonName: 'global_header_saved_search',
          })
        }
      }
    },
    [trackButtonClicked]
  )

  const openDropdownState = useOpenDropdownState()

  const watchlistProps = useWatchlist({
    personId,
    isAuthenticated,
    openDropdownState,
  })

  const savedSearch = useSavedSearch({
    accountId,
    personId,
    isAuthenticated,
    openDropdownState,
  })

  const { notificationsSlot, notificationOverlay } = useNotifications({
    accountId,
    personId,
    isAuthenticated,
    openDropdownState,
  })

  return (
    <>
      <GlobalHeaderStyleOverride onClick={handleLogoClick}>
        <GlobalHeader
          portalName={portalName}
          onAnalyticsEvent={handleAnalyticsEvent}
          showOffersLink={showOffersLink}
          showInventoryLink={showInventoryLink}
          onMenuItemClick={handleMenuItemClick}
          navData={navData}
          notificationsSlot={notificationsSlot}
          auctionNavigation={
            showAuctionNav ? (
              // key resets fe-core's internal mobileOpenKey state on every
              // navigation (BP-2167). useEffect-based approaches fire after
              // paint and leave the submenu briefly visible on the new page.
              <GlobalHeaderAuctionNavigation
                key={`${pathname ?? ''}?${searchParams?.toString() ?? ''}`}
                cms={auctionNav}
              />
            ) : undefined
          }
          {...searchProps}
          {...authProps}
          {...watchlistProps}
          {...savedSearch.headerProps}
        />
      </GlobalHeaderStyleOverride>
      {savedSearch.modal}
      {notificationOverlay}
    </>
  )
}
